/**
 * Aspect — LUT color grading (post-processing stack)
 * --------------------------------------------------------------
 * Builds two custom 256x16 color-grading LUT strip textures (the format the
 * PlayCanvas CameraFrame compose pass expects: 16 slices of 16x16, blue in
 * slices, red across, green down) and attaches them to the CameraFrame's
 * native dual-LUT slots:
 *
 *   colorLUT.texture   day grade   — warm lift, gentle film S-curve
 *   colorLUT.texture2  night grade — cool teal shadows, deeper toe
 *   colorLUT.blend     driven by the global timeline (0 = day, 1 = night)
 *
 * Vignette already lives on the same CameraFrame (see setupPostProcessing);
 * this module only adds the LUT pair and keeps it wired across quality
 * changes (the frame is rebuilt when the user switches render quality).
 */

import * as pc from "playcanvas";
import { hourToTimeBlend } from "./baked_lighting.js";

const LUT_N = 16;
const LUT_W = LUT_N * LUT_N; // 256

const clamp01 = (v) => Math.max(0, Math.min(1, v));

/** Smooth filmic S-curve around 0.5 (k = strength). */
function sCurve(v, k) {
  return clamp01(v + k * (v - 0.5) * (1 - Math.abs(2 * v - 1)));
}

function lift(v, amount) {
  return clamp01(v + amount * (1 - v));
}

/** Day grade: subtle warmth and gentle contrast, open shadows. */
function gradeDay(r, g, b) {
  r = sCurve(r, 0.1);
  g = sCurve(g, 0.09);
  b = sCurve(b, 0.08);
  r = lift(r, 0.015);
  return [clamp01(r * 1.03), clamp01(g * 1.0), clamp01(b * 0.97)];
}

/** Night grade: cooled highlights, lifted teal shadows (no crushed blacks). */
function gradeNight(r, g, b) {
  r = sCurve(r, 0.06) * 0.96;
  g = sCurve(g, 0.06) * 0.99;
  b = sCurve(b, 0.05) * 1.05;
  r = lift(r, 0.02);
  g = lift(g, 0.035);
  b = lift(b, 0.06);
  return [clamp01(r), clamp01(g), clamp01(b)];
}

/** Render a grading function into a 256x16 LUT strip canvas. */
function paintLut(grade) {
  const canvas = document.createElement("canvas");
  canvas.width = LUT_W;
  canvas.height = LUT_N;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(LUT_W, LUT_N);
  for (let slice = 0; slice < LUT_N; slice++) {
    const b = slice / (LUT_N - 1);
    for (let y = 0; y < LUT_N; y++) {
      const g = y / (LUT_N - 1);
      for (let x = 0; x < LUT_N; x++) {
        const r = x / (LUT_N - 1);
        const [rr, gg, bb] = grade(r, g, b);
        const i = (y * LUT_W + slice * LUT_N + x) * 4;
        img.data[i] = Math.round(rr * 255);
        img.data[i + 1] = Math.round(gg * 255);
        img.data[i + 2] = Math.round(bb * 255);
        img.data[i + 3] = 255;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function lutTexture(device, canvas, name) {
  const tex = new pc.Texture(device, {
    name,
    width: canvas.width,
    height: canvas.height,
    format: pc.PIXELFORMAT_SRGBA8 ?? pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: false,
    minFilter: pc.FILTER_LINEAR,
    magFilter: pc.FILTER_LINEAR,
    addressU: pc.ADDRESS_CLAMP_TO_EDGE,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
  });
  tex.setSource(canvas);
  return tex;
}

export class LutGrading {
  /**
   * @param {pc.AppBase} app
   * @param {{ frame: object|null }} postFx controller from setupPostProcessing
   */
  constructor(app, postFx) {
    this.app = app;
    this.postFx = postFx;
    this.dayLut = lutTexture(app.graphicsDevice, paintLut(gradeDay), "lut-day");
    this.nightLut = lutTexture(app.graphicsDevice, paintLut(gradeNight), "lut-night");
    this._blend = 0;

    this._attach();
    // The CameraFrame is torn down + rebuilt on render-quality changes.
    this._onQuality = () => this._attach();
    app.on("ui:render-quality", this._onQuality);
    // Same global timeline as the lightmap / probe / reflection blends.
    this._onTime = ({ hour }) => this.setBlend(hourToTimeBlend(hour));
    app.on("time:changed", this._onTime);
  }

  _attach() {
    const frame = this.postFx?.frame;
    if (!frame?.colorLUT) return;
    frame.colorLUT.texture = this.dayLut;
    frame.colorLUT.intensity = 0.85;
    frame.colorLUT.texture2 = this.nightLut;
    frame.colorLUT.intensity2 = 0.9;
    frame.colorLUT.blend = this._blend;
    frame.update();
    this.app.renderNextFrame = true;
  }

  /** Timeline blend between the day LUT (0) and night LUT (1). */
  setBlend(t) {
    this._blend = clamp01(t);
    const frame = this.postFx?.frame;
    if (!frame?.colorLUT) return;
    frame.colorLUT.blend = this._blend;
    frame.update();
    this.app.renderNextFrame = true;
  }

  destroy() {
    this.app.off("ui:render-quality", this._onQuality);
    this.app.off("time:changed", this._onTime);
    const frame = this.postFx?.frame;
    if (frame?.colorLUT) {
      frame.colorLUT.texture = null;
      frame.colorLUT.texture2 = null;
      frame.update();
    }
    this.dayLut.destroy();
    this.nightLut.destroy();
  }
}
